// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class DeadlinesService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
  ) { }

  /**
   * Get deadlines list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSubscriptionsList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    // Force V2: the subscription deadlines list is V2-shaped (consumes data.items)
    // and the search[forfait] filter is only honored by the V2 endpoint
    // (V1 uses forfait solely for sorting).
    return this.apiService.apiCall('GET', 'deadlines/subscriptions', params, undefined, undefined, undefined, 'v2');
  }

  /**
   * Get deadlines list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getDocumentsList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'deadlines/documents', params);
  }
  /**
   * Get deadlines list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSubscriptionsSuppliersList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'deadlines/subscriptions-suppliers', params);
  }

  /**
     * Get deadlines list for given query params
     *
     * @since 1.0.0
     *
     * @param [propertyId] Query left offset
     * @param [start] Query left offset
     * @param [limit] Results number
     * @param [sort] Sorting field name
     * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
     * @param [search] Filtering object with fildering data string
     */
  getDocumentsPopertyList(propertyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `deadlines/property/${propertyId}/documents`, params);
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @since 1.0.0
   */
  getSubscriptionsExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'deadlines/export-subscriptions', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @since 1.0.0
   */
  getSubscriptionsSuppliersExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'deadlines/export-subscriptions-suppliers', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  
  /**
   * Get an exportation file of all deadlines
   *
   * @since 1.0.0
   */
  getSubscriptionsNfcAccessExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'deadlines/export-subscriptions-nfc-access', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @since 1.0.0
   */
  getSubscriptionsPropertiesExport(companyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `deadlines/export-subscriptions-properties/${companyId}`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @since 1.0.0
   */
  getSubscriptionsNfcPropertiesExport(companyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `deadlines/export-subscriptions-nfc-properties/${companyId}`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @since 1.0.0
   */
  getDocumentsExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'deadlines/export-documents', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @since 1.0.0
   */
  getPropertyDocumentsExport(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `deadlines/export-property-documents/${propertyId}`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @param data List of properties to renew subscriptions
   * @since 1.0.0
   */
  renewSubscriptionsDeadline(data: object): Observable<any> {
    return this.apiService.apiCall("PATCH",
      'deadlines/renew-subscriptions',
      data
    );
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @param data List of properties to renew subscriptions
   * @since 1.0.0
   */
  renewSubscriptionsSuppliersDeadline(data: object): Observable<any> {
    return this.apiService.apiCall("PATCH",
      'deadlines/renew-subscriptions-suppliers',
      data
    );
  }
  
  /**
   * Get deadlines list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getNfcAccessSubscriptionsList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'deadlines/subscriptions-nfc-access', params);
  }

  /**
   * Get an exportation file of all deadlines
   *
   * @param data List of properties to renew subscriptions
   * @since 1.0.0
   */
  renewSubscriptionsNfcAccessDeadline(data: object): Observable<any> {
    return this.apiService.apiCall("PATCH",
      'deadlines/renew-subscriptions-nfc-access',
      data
    );
  }

  /**
   * Get deadlines list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSubscriptionsPrivacyList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'deadlines/subscriptions-privacy', params);
  }

  /**
   * Get an exportation file of all privacy deadlines
   *
   * @since 1.0.0
   */
  getSubscriptionsPrivacyExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'deadlines/export-subscriptions-privacy', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all properties paying privacy subscription for the given company
   *
   * @since 1.0.0
   */
  getSubscriptionsPrivacyPropertiesExport(companyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `deadlines/export-subscriptions-privacy-properties/${companyId}`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Renew the privacy deadline for the given list of companies
   *
   * @param data List of companies to renew privacy subscriptions
   * @since 1.0.0
   */
  renewSubscriptionsPrivacyDeadline(data: object): Observable<any> {
    return this.apiService.apiCall("PATCH",
      'deadlines/renew-subscriptions-privacy',
      data
    );
  }

  /**
   * Get deadlines list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSubscriptionsPrivacySagList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'deadlines/subscriptions-privacy-sag', params);
  }

  /**
   * Get an exportation file of all privacy sag deadlines
   *
   * @since 1.0.0
   */
  getSubscriptionsPrivacySagExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'deadlines/export-subscriptions-privacy-sag', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
}
